<template>
  <b-modal
    v-model="showModal"
    size="md"
    id="custom-barcodes-modal"
    title="Custom barcodes"
    ok-title="Save"
    cancel-variant="outline-secondary"
    :ok-disabled="isFormLoading"
    :cancel-disabled="isFormLoading"
    @ok.prevent="onSave"
  >
    <div
      v-if="isFormLoading"
      class="modal-overlay d-flex align-items-center justify-content-center"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
    </div>

    <b-form>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Barcode"
            label-for="custom-barcode-input"
          >
            <BarcodeTargetField
              hint-key="custom-barcode"
              :active="showModal"
            >
              <b-form-input
                id="custom-barcode-input"
                class="barcode-target"
                v-model="form.barcode"
                placeholder="Barcode"
                type="text"
                maxlength="100"
                :state="formError ? false : null"
                @input="formError = ''"
              />
            </BarcodeTargetField>
            <small
              v-if="formError"
              class="text-danger"
            >{{ formError }}</small>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Description"
            label-for="custom-barcode-description"
          >
            <b-form-textarea
              id="custom-barcode-description"
              v-model="form.description"
              placeholder="Description (optional)"
              rows="2"
              max-rows="4"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col cols="12">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="addBarcode"
          >
            Add another barcode
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="barcodes.length > 0"
        class="custom-barcodes-list border rounded p-1"
      >
        <div
          v-for="(item, index) in barcodes"
          :key="`${item.barcode}-${index}`"
          class="d-flex align-items-start justify-content-between py-50 custom-barcode-row"
        >
          <div class="pr-1">
            <div class="font-weight-bold">{{ item.barcode }}</div>
            <small
              v-if="item.description"
              class="text-muted"
            >{{ item.description }}</small>
          </div>
          <b-button
            variant="outline-danger"
            size="sm"
            class="flex-shrink-0"
            @click="removeBarcode(index)"
          >
            X
          </b-button>
        </div>
      </div>
      <p
        v-else
        class="text-muted mb-0"
      >
        No custom barcodes yet.
      </p>
    </b-form>
  </b-modal>
</template>

<script setup>
import { getCurrentInstance, nextTick, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

const props = defineProps({
  clickDate: {
    type: Date,
    default: null,
  },
  unitId: {
    type: [Number, String],
    default: null,
  },
  initialBarcodes: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['saved'])

const store = useStore()
const instance = getCurrentInstance()

const showModal = ref(false)
const isFormLoading = ref(false)
const formError = ref('')
const barcodes = ref([])
const form = reactive({
  barcode: '',
  description: '',
})

const normalizeList = (list) => {
  if (!Array.isArray(list)) {
    return []
  }

  return list
    .map((item) => ({
      id: item?.id ?? null,
      barcode: String(item?.barcode ?? '').trim(),
      description: item?.description != null ? String(item.description).trim() : '',
    }))
    .filter((item) => item.barcode !== '')
}

const resetForm = () => {
  form.barcode = ''
  form.description = ''
  formError.value = ''
}

const focusBarcodeField = () => {
  nextTick(() => {
    const input = document.getElementById('custom-barcode-input')
    input?.focus()
    input?.select?.()
  })
}

const openModal = () => {
  barcodes.value = normalizeList(props.initialBarcodes)
  resetForm()
  showModal.value = true
  focusBarcodeField()
}

watch(
  () => props.clickDate,
  (value) => {
    if (value) {
      openModal()
    }
  },
)

const addBarcode = () => {
  const barcode = String(form.barcode || '').trim()
  if (!barcode) {
    formError.value = 'Barcode is required'
    return
  }

  const duplicate = barcodes.value.some(
    (item) => item.barcode.toLowerCase() === barcode.toLowerCase(),
  )
  if (duplicate) {
    formError.value = 'This barcode is already in the list'
    return
  }

  barcodes.value.push({
    id: null,
    barcode,
    description: String(form.description || '').trim(),
  })
  resetForm()
  focusBarcodeField()
}

const removeBarcode = async (index) => {
  const item = barcodes.value[index]
  if (!item) {
    return
  }

  const confirmed = await instance.proxy.$bvModal.msgBoxConfirm(
    `Remove barcode "${item.barcode}"?`,
    {
      title: 'Please Confirm',
      okVariant: 'danger',
      okTitle: 'Remove',
      cancelTitle: 'Cancel',
    },
  )

  if (!confirmed) {
    return
  }

  barcodes.value.splice(index, 1)
}

const flushPendingForm = () => {
  const barcode = String(form.barcode || '').trim()
  if (!barcode) {
    return { ok: true, list: [...barcodes.value] }
  }

  const duplicate = barcodes.value.some(
    (item) => item.barcode.toLowerCase() === barcode.toLowerCase(),
  )
  if (duplicate) {
    formError.value = 'This barcode is already in the list'
    return { ok: false, list: [...barcodes.value] }
  }

  return {
    ok: true,
    list: [
      ...barcodes.value,
      {
        id: null,
        barcode,
        description: String(form.description || '').trim(),
      },
    ],
  }
}

const validationErrorMessage = (error) => {
  const data = error?.response?.data
  if (!data) {
    return 'Something went wrong'
  }

  const fieldErrors = data.errors
  if (fieldErrors && typeof fieldErrors === 'object') {
    const first = Object.values(fieldErrors).flat().find(Boolean)
    if (first) {
      return String(first)
    }
  }

  if (typeof data.barcodes === 'string') {
    return data.barcodes
  }
  if (Array.isArray(data.barcodes) && data.barcodes[0]) {
    return String(data.barcodes[0])
  }

  const parsed = backendErrorString(error)
  if (parsed && !parsed.includes('[object Object]')) {
    return parsed
  }

  return data.message || 'Something went wrong'
}

const onSave = async () => {
  const pending = flushPendingForm()
  if (!pending.ok) {
    return
  }

  const payload = pending.list.map((item) => ({
    barcode: item.barcode,
    description: item.description || null,
  }))

  if (!props.unitId) {
    barcodes.value = pending.list
    resetForm()
    emit('saved', payload)
    showModal.value = false
    return
  }

  isFormLoading.value = true
  try {
    const response = await store.dispatch('units/syncCustomBarcodes', {
      unitId: props.unitId,
      barcodes: payload,
    })
    const saved = normalizeList(response?.data ?? payload)
    barcodes.value = saved
    resetForm()
    emit('saved', saved)
    toastSuccess('Custom barcodes saved')
    showModal.value = false
  } catch (error) {
    barcodes.value = normalizeList(props.initialBarcodes)
    const message = validationErrorMessage(error)
    formError.value = message
    toastFailure(message)
  } finally {
    isFormLoading.value = false
  }
}
</script>

<style scoped>
.custom-barcode-row + .custom-barcode-row {
  border-top: 1px solid #e7e7e7;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}
</style>
